import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { Factura, ProductoServicio, type MetodoEntregaFactura } from '../components/facturas/types';
import { handleStoreAction } from '../utils/storeHelpers';
import { logger } from '../utils/logger';
import { createMoraAlert, createDeliveryLateAlert } from '../lib/alerts/alertGenerator';
import { anularFactura as anularFacturaEnSupabase } from '../hooks/useDevoluciones';
import { invokeSendPush } from '../lib/push/sendPushNotification';
import { generarNumeroFacturaBorrador, numeroFacturaParaMostrar } from '../utils/facturaNumeroCanal';
import { parseHistorialPagos, historialPagosToJsonDb } from '../utils/facturaRegistrarPago';
import { facturaEsPedidoOnlineVerificable } from '../utils/facturaVerificacionOnline';
import {
  crearCreditoTrasFactura,
  sincronizarCreditoTrasUpdateFactura,
} from '../lib/credito/crearCreditoTrasFactura';
import type { CreditoCrearConfig } from '../components/facturas/types';
import { normalizeMetodoPagoDbValue } from '../utils/metodoPagoDbNormalize';
import { estadoPagoEsPagado, normalizeEstadoPagoAlmacenado } from '../shared/normalizeEstadoPago';
import { assertMonedaPermitidaParaEmpresa } from '../utils/empresaMoneda';
import { cerrarPedidoEnLineaPorFactura } from '../shared/pedido/cerrarPedidoEnLinea';
import {
  asegurarCreditoTrasCompletarVenta,
  completarVentaDebeCrearCredito,
  saldoPendienteFactura,
} from '../shared/pedido/asegurarCreditoTrasCompletar';

/** Items en DB como jsonb, texto JSON o vacío — nunca debe fallar el parseo. */
function parseFacturaItems(raw: unknown): Factura['items'] {
  if (raw == null || raw === '') return [];
  try {
    const v = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return Array.isArray(v) ? (v as Factura['items']) : [];
  } catch {
    return [];
  }
}

function numDb(v: unknown): number {
  if (v == null || v === '') return 0;
  const n = Number(v);
  return Number.isNaN(n) ? 0 : n;
}

function normalizeMetodoEntregaDb(raw: unknown, tipoEntrega?: string | null): MetodoEntregaFactura {
  const s = String(raw ?? '').trim().toLowerCase();
  if (s === 'recogida' || s === 'entrega_local' || s === 'envio') return s;
  const t = String(tipoEntrega ?? '').toUpperCase();
  if (t === 'DOMICILIO') return 'entrega_local';
  return 'recogida';
}

const ENTREGADO_RESUELTO = new Set(['ENTREGADO', 'ENTREGADO_PARCIAL', 'EN_TIENDA']);

function isEntregaResuelta(estado: unknown): boolean {
  return ENTREGADO_RESUELTO.has(String(estado ?? '').toUpperCase());
}

/** Quita alertas de cron cuando la factura ya no está en mora o la entrega ya no está tardía. */
async function deleteAlertsFacturaCondicionResuelta(
  facturaId: string,
  opts: { entregaOk?: boolean; moraOk?: boolean }
) {
  if (!facturaId) return;
  if (opts.entregaOk) {
    const { error } = await supabase
      .from('alerts')
      .delete()
      .eq('related_entity_id', facturaId)
      .eq('type', 'entrega_tarde');
    if (error) logger.warn('delete entrega_tarde alert:', error.message);
  }
  if (opts.moraOk) {
    const { error } = await supabase
      .from('alerts')
      .delete()
      .eq('related_entity_id', facturaId)
      .eq('type', 'factura_vencida');
    if (error) logger.warn('delete factura_vencida alert:', error.message);
  }
}

/**
 * Saldo pendiente coherente con total y pagos:
 * - `monto_pendiente` NULL o filas viejas → derivar de total − monto_pagado.
 * - A veces la DB guarda `monto_pendiente = 0` pero `estado_pago` sigue sin ser PAGADO;
 *   en ese caso no confiar solo en la columna: usar max(explícito, derivado).
 * - Si `estado_pago` es PAGADO, el pendiente es 0.
 */
function montoPendienteDesdeFila(f: {
  total?: unknown;
  monto_pagado?: unknown;
  monto_pendiente?: unknown;
  saldo_pendiente?: unknown;
  estado_pago?: unknown;
}): number {
  const total = numDb(f.total);
  const pagado = numDb(f.monto_pagado);
  const derived = Math.max(0, total - pagado);
  if (estadoPagoEsPagado(f.estado_pago)) return 0;

  const raw = f.monto_pendiente;
  let explicit = 0;
  if (raw != null && raw !== '') {
    const n = Number(raw);
    if (!Number.isNaN(n)) explicit = Math.max(0, n);
  }
  const rawSaldo = f.saldo_pendiente;
  if (rawSaldo != null && rawSaldo !== '') {
    const n = Number(rawSaldo);
    if (!Number.isNaN(n)) explicit = Math.max(explicit, Math.max(0, n));
  }
  return Math.max(explicit, derived);
}

function mapFilaSupabaseALocalFactura(
  f: any,
  clientesMap: Map<string, { id: string; nombre?: string; apellido?: string; telefono?: string; email?: string; numero_documento?: string }>
): Factura {
  logger.debug('🔍 Processing factura:', f.id, 'cliente_id:', f.cliente_id);
  if (f.cliente_id) {
    const c = clientesMap.get(f.cliente_id);
    logger.debug('🔍 Client lookup for factura', f.id, 'cliente_id:', f.cliente_id, 'found:', !!c, 'cliente data:', c);
  }
  return {
    id: f.id,
    numero: f.numero,
    cliente: f.cliente_id
      ? (() => {
          const cliente = clientesMap.get(f.cliente_id);
          return cliente
            ? {
                id: cliente.id,
                empresaId: f.empresa_id,
                nombre: cliente.nombre || 'Sin nombre',
                apellido: cliente.apellido || '',
                telefono: cliente.telefono || 'N/A',
                email: cliente.email || 'N/A',
                numeroDocumento: cliente.numero_documento || 'N/A',
              }
            : null;
        })()
      : null,
    fechaCreacion: f.fecha ? new Date(f.fecha) : new Date(),
    fechaEntrega: f.vencimiento ? new Date(f.vencimiento) : new Date(),
    estadoPago: normalizeEstadoPagoAlmacenado(f.estado_pago),
    estadoEntrega: f.estado_entrega || 'PENDIENTE_ENTREGA',
    estado: (f.estado as Factura['estado']) || 'ACTIVA',
    items: parseFacturaItems(f.items),
    subtotalSinIva: f.subtotal || 0,
    totalIva: f.impuestos || 0,
    subtotal: f.subtotal || 0,
    descuento: f.descuentos || 0,
    total: f.total || 0,
    notas: f.notas || '',
    condiciones: '',
    metodoPago: f.metodo_pago || '', // Fixed: Don't default to EFECTIVO
    comprobanteUrl: f.comprobante_url || undefined,
    fechaPago: f.fecha_pago ? new Date(f.fecha_pago) : undefined,
    fechaEntregaReal: f.fecha_entrega ? new Date(f.fecha_entrega) : undefined,
    mensajesEnviados: [],
    ultimaActividad: f.ultima_actividad
      ? new Date(f.ultima_actividad)
      : f.updated_at
        ? new Date(f.updated_at)
        : new Date(),
    montoPagado: numDb(f.monto_pagado),
    montoPendiente: montoPendienteDesdeFila(f),
    historialPagos: parseHistorialPagos(f.historial_pagos),
    motivoCancelacion: f.motivo_cancelacion || '',
    fechaCancelacion: f.fecha_cancelacion ? new Date(f.fecha_cancelacion) : undefined,
    tipoEntrega: f.tipo_entrega || ('TIENDA' as const),
    metodoEntrega: normalizeMetodoEntregaDb((f as { metodo_entrega?: string }).metodo_entrega, f.tipo_entrega),
    costoEnvio: Math.round(numDb((f as { costo_envio?: unknown }).costo_envio)),
    notasEntrega: String((f as { notas_entrega?: string | null }).notas_entrega ?? '').trim() || undefined,
    ventaCompletada: Boolean((f as { venta_completada?: boolean }).venta_completada),
    ventaCompletadaAt: (f as { venta_completada_at?: string }).venta_completada_at
      ? new Date(String((f as { venta_completada_at?: string }).venta_completada_at))
      : undefined,
    feEstado: (f.fe_estado as Factura['feEstado']) || null,
    feCufe: f.fe_cufe ? String(f.fe_cufe) : null,
    feError: f.fe_error ? String(f.fe_error) : null,
    feEmittedAt: f.fe_emitted_at ? new Date(String(f.fe_emitted_at)) : null,
    direccionEntrega: f.direccion_entrega || '',
    ciudadEntrega: f.ciudad_entrega || '',
    barrioEntrega: f.barrio_entrega || '',
    instruccionesEntrega: f.instrucciones_entrega || '',
    contactoEntrega: f.contacto_entrega || '',
    telefonoEntrega: f.telefono_entrega || '',
    tipoDescuento: f.tipo_descuento || ('NINGUNO' as const),
    valorDescuento: f.valor_descuento || 0,
    descripcionDescuento: f.descripcion_descuento || '',
    citaId: f.cita_id || undefined,
    fechaAgendada: f.fecha_agendada ? new Date(f.fecha_agendada) : undefined,
    fechaEntregaAgendada: f.fecha_entrega_agendada ? new Date(f.fecha_entrega_agendada) : undefined,
    horaAgendada: f.hora_agendada || '',
    vendedor: f.vendedor || '',
    canalVenta: f.canal_venta || ('TIENDA' as const),
    canalOrigen: f.canal_origen || null,
    estadoVerificacion: (f as { estado_verificacion?: Factura['estadoVerificacion'] }).estado_verificacion ?? null,
    prioridad: f.prioridad || ('MEDIA' as const),
    requiereInstalacion: f.requiere_instalacion || false,
    garantia: f.garantia || '',
    observacionesInternas: f.observaciones_internas || '',
    esRegalo: f.es_regalo || false,
    mensajeRegalo: f.mensaje_regalo || '',
    direccionRegalo: f.direccion_regalo || '',
    motivoRegalo: f.motivo_regalo || '',
    destinatarioRegalo: f.destinatario_regalo || '',
    ocasionRegalo: f.ocasion_regalo || ('OTRO' as const),
    fechaOcasion: f.fecha_ocasion ? new Date(f.fecha_ocasion) : undefined,
    incluirTarjetaRegalo: f.incluir_tarjeta_regalo || false,
    mensajeTarjeta: f.mensaje_tarjeta || '',
    fotoEntrega: f.foto_entrega ?? null,
  };
}

// No mock data - all data comes from Supabase
const mockFacturas: Factura[] = [];

// Mock products and services data
const mockProductosServicios: ProductoServicio[] = [];

interface FacturasState {
  facturas: Factura[];
  facturasArchivadas: Factura[];
  productosServicios: ProductoServicio[];
  loading: boolean;
  error: string | null;
  
  // Actions
  fetchFacturas: (empresaId: string) => Promise<Factura[] | null | void>;
  /** Una factura por `numero` (misma forma que en listado), sin tocar `loading` del store. */
  fetchFacturaPorNumero: (empresaId: string, numero: string) => Promise<Factura | null>;
  fetchFacturaPorId: (empresaId: string, facturaId: string) => Promise<Factura | null>;
  fetchFacturasArchivadas: (empresaId: string) => Promise<any[] | null | void>;
  createFactura: (factura: Omit<Factura, 'id' | 'createdAt' | 'updatedAt'>) => Promise<Factura | null | void>;
  updateFactura: (id: string, factura: Partial<Factura>) => Promise<Factura | null | void>;
  deleteFactura: (id: string) => Promise<void | null>;
  archivarFactura: (id: string) => Promise<void | null>;
  deleteArchivadasOldas: (empresaId: string) => Promise<void>;
  /** Anula factura en BD (restaura inventario vía trigger) y actualiza estado local. */
  anularFactura: (facturaId: string) => Promise<void | null | void>;
  /**
   * Solo UPDATE sobre factura existente + INSERT en pagos_factura.
   * Nunca crea una factura nueva ni modifica stock.
   */
  registrarPago: (facturaId: string, monto: number, metodoPago: string) => Promise<void | null>;
  /** Pedido online: confirma comprobante → PAGADO + disparadores (venta + stock). */
  verificarPago: (facturaId: string) => Promise<void | null>;
  rechazarPago: (facturaId: string, motivo: string) => Promise<void | null>;
  /** Solo UPDATE estado_entrega / fecha_entrega; nunca INSERT factura. */
  marcarEntrega: (facturaId: string, estadoEntrega: Factura['estadoEntrega']) => Promise<void | null>;
  /**
   * Cierra el pedido en el libro (sale de Pedidos) sin forzar pago completo.
   * Archiva la factura y marca `venta_completada` (saldo puede seguir en Cobros y Créditos).
   */
  completarVentaPedido: (
    facturaId: string,
    opts?: { fechaVencimientoCredito?: string }
  ) => Promise<void | null>;
  /** Baja lógica del pedido (estado ELIMINADA); paridad app móvil. */
  eliminarPedido: (facturaId: string) => Promise<void | null>;
  clearError: () => void;
  reset: () => void;
}

export const useFacturasStore = create<FacturasState>((set, get) => ({
  facturas: mockFacturas,
  facturasArchivadas: [],
  productosServicios: mockProductosServicios,
  loading: false,
  error: null,

  fetchFacturas: async (empresaId: string) => {
    return handleStoreAction(
      async () => {
        if (!empresaId) {
          logger.warn('❌ No empresa ID provided to fetchFacturas');
          set({ facturas: [] });
          return;
        }

        logger.debug('🔍 Fetching facturas for empresa:', empresaId);

        const { supabase } = await import('../lib/supabase');

        // Fetch facturas from Supabase
        // Filter: Show all facturas except explicitly archived/deleted
        // We'll filter archived ones in the frontend based on vistaActual state
        const { data: supabaseFacturas, error: fetchError } = await supabase
          .from('facturas')
          .select('*')
          .eq('empresa_id', empresaId)
          .order('fecha', { ascending: false });

        if (fetchError) {
          logger.error('🔍 FETCH ERROR DETAILS:', {
            message: fetchError.message,
            details: fetchError.details,
            hint: fetchError.hint,
            code: fetchError.code
          });
          throw fetchError;
        }

        logger.info(`📄 Facturas cargadas desde Supabase para empresa ${empresaId}: ${supabaseFacturas?.length || 0} facturas`);
        if (supabaseFacturas && supabaseFacturas.length > 0) {
          logger.debug('📄 Primeras facturas:', supabaseFacturas.slice(0, 3).map(f => ({ id: f.id, numero: f.numero, estado: f.estado })));
        }

        // Fetch clients separately and create a lookup map
        const { data: clientes, error: clientesError } = await supabase
          .from('clientes')
          .select('id, nombre, apellido, telefono, email, numero_documento')
          .eq('empresa_id', empresaId);

        if (clientesError) {
          logger.error('❌ Error fetching clientes:', clientesError);
        }

        const clientesMap = new Map();
        if (clientes) {
          clientes.forEach(cliente => {
            clientesMap.set(cliente.id, cliente);
          });
        }

        logger.debug('👥 Clientes cargados:', clientesMap.size);
        logger.debug('🔍 Clientes map contents:', Array.from(clientesMap.entries()));
      
        // Convert to local format
        const facturasLocales = (supabaseFacturas || []).map((f: any) =>
          mapFilaSupabaseALocalFactura(f, clientesMap)
        );

        set({ facturas: facturasLocales });
      },
      (error) => set({ error }),
      (loading) => set({ loading }),
      {
        errorPrefix: 'Error al cargar facturas desde Supabase'
      }
    );
  },

  fetchFacturaPorId: async (empresaId: string, facturaId: string) => {
    const id = String(facturaId || '').trim();
    if (!empresaId || !id) return null;
    try {
      const { data: f, error } = await supabase
        .from('facturas')
        .select('*')
        .eq('empresa_id', empresaId)
        .eq('id', id)
        .maybeSingle();
      if (error) {
        logger.warn('fetchFacturaPorId:', error);
        return null;
      }
      if (!f) return null;
      const { data: cliente } = f.cliente_id
        ? await supabase
            .from('clientes')
            .select('id, nombre, apellido, telefono, email, numero_documento')
            .eq('id', f.cliente_id)
            .maybeSingle()
        : { data: null };
      const clientesMap = new Map<
        string,
        { id: string; nombre?: string; apellido?: string; telefono?: string; email?: string; numero_documento?: string }
      >();
      if (cliente) clientesMap.set(cliente.id, cliente);
      return mapFilaSupabaseALocalFactura(f, clientesMap);
    } catch (e) {
      logger.error('fetchFacturaPorId', e);
      return null;
    }
  },

  fetchFacturaPorNumero: async (empresaId: string, numero: string) => {
    const n = String(numero || '').trim();
    if (!empresaId || !n) return null;
    const tryNumero = async (candidate: string) => {
      const { data: f, error } = await supabase
        .from('facturas')
        .select('*')
        .eq('empresa_id', empresaId)
        .eq('numero', candidate)
        .maybeSingle();
      if (error) {
        logger.warn('fetchFacturaPorNumero:', error);
        return null;
      }
      return f;
    };
    try {
      let f = await tryNumero(n);
      if (!f) {
        const { nucleoNumeroFactura } = await import('../utils/facturaNumeroCanal');
        const core = nucleoNumeroFactura(n);
        if (core && core !== n) f = await tryNumero(core);
      }
      if (!f) return null;
      const { data: cliente } = f.cliente_id
        ? await supabase
            .from('clientes')
            .select('id, nombre, apellido, telefono, email, numero_documento')
            .eq('id', f.cliente_id)
            .maybeSingle()
        : { data: null };
      const clientesMap = new Map<
        string,
        { id: string; nombre?: string; apellido?: string; telefono?: string; email?: string; numero_documento?: string }
      >();
      if (cliente) clientesMap.set(cliente.id, cliente);
      return mapFilaSupabaseALocalFactura(f, clientesMap);
    } catch (e) {
      logger.error('fetchFacturaPorNumero', e);
      return null;
    }
  },

  createFactura: async (factura) => {
    return handleStoreAction(
      async () => {
        logger.debug('🔄 Creating factura:', factura);

        // Get empresa ID from currentEmpresa (passed via factura or from AuthStore)
        const { useAuthStore } = await import('./useAuthStore');
        const authStore = useAuthStore.getState();
        const currentEmpresa = authStore.currentEmpresa;
        
        if (!currentEmpresa?.id) {
          throw new Error('No empresa seleccionada');
        }

        const empresaId = currentEmpresa.id;
        // CO rows must stay COP even if config JSON or payload tries otherwise (pre-migration gate).
        assertMonedaPermitidaParaEmpresa(
          currentEmpresa,
          (factura as { moneda?: string | null }).moneda,
        );
        logger.debug('👤 Creating factura for empresa:', empresaId);

        const canal = factura.canalVenta || 'TIENDA';
        const rawNumIn = (factura.numero || '').trim();
        const numeroNormalizado = rawNumIn
          ? numeroFacturaParaMostrar(rawNumIn, canal)
          : generarNumeroFacturaBorrador(canal);

        const t = factura.nuevaFlujoTotales;
        const totalNum = t ? t.total : factura.total || 0;
        const paidNum =
          factura.montoPagado != null ? numDb(factura.montoPagado) : 0;
        const saldoNum =
          factura.montoPendiente != null
            ? numDb(factura.montoPendiente)
            : Math.max(0, totalNum - paidNum);
      // Convert to Supabase format
      const supabaseFactura: Record<string, unknown> = {
        empresa_id: empresaId,
        cliente_id: factura.cliente?.id || null,
        numero: numeroNormalizado,
        canal_venta: canal,
        fecha: (factura.fechaCreacion || new Date()).toISOString(),
        vencimiento: factura.fechaEntrega ? factura.fechaEntrega.toISOString() : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        subtotal: t ? t.base_iva : factura.subtotal || 0,
        impuestos: t ? t.iva : factura.totalIva || 0,
        descuentos: t ? t.descuento : factura.descuento || 0,
        total: totalNum,
        estado: factura.estado || 'ACTIVA',
        estado_pago: factura.estadoPago || 'PENDIENTE_PAGO', // Added: Save payment status
        metodo_pago: (() => {
          const mp = factura.metodoPago;
          if (mp == null || String(mp).trim() === '') return null;
          return normalizeMetodoPagoDbValue(String(mp)) ?? String(mp);
        })(),
        notas: factura.notas || '',
        items: JSON.stringify(factura.items || []), // Store items as JSON string
        updated_at: new Date().toISOString(),
        monto_pagado: paidNum,
        monto_pendiente: saldoNum,
        saldo_pendiente: saldoNum,
        es_regalo: !!factura.esRegalo,
        ...(factura.mensajeRegalo != null && String(factura.mensajeRegalo).trim() !== ''
          ? { mensaje_regalo: String(factura.mensajeRegalo).trim() }
          : {}),
        ...(factura.direccionRegalo != null && String(factura.direccionRegalo).trim() !== ''
          ? { direccion_regalo: String(factura.direccionRegalo).trim() }
          : {}),
        ...(factura.fechaEntregaAgendada
          ? { fecha_entrega_agendada: factura.fechaEntregaAgendada.toISOString() }
          : {}),
        ...(factura.fechaPago ? { fecha_pago: factura.fechaPago.toISOString() } : {}),
        ...(factura.tipoEntrega ? { tipo_entrega: factura.tipoEntrega } : {}),
        ...(factura.direccionEntrega ? { direccion_entrega: factura.direccionEntrega } : {}),
        metodo_entrega:
          factura.metodoEntrega ||
          (factura.tipoEntrega === 'DOMICILIO' ? 'entrega_local' : 'recogida'),
        costo_envio: Math.round(numDb(factura.costoEnvio)),
        ...(factura.notasEntrega != null && String(factura.notasEntrega).trim() !== ''
          ? { notas_entrega: String(factura.notasEntrega).trim() }
          : {}),
      };

        const epCreacion = String(factura.estadoPago || 'PENDIENTE_PAGO').toUpperCase();
        if (facturaEsPedidoOnlineVerificable({ numero: numeroNormalizado }) && epCreacion !== 'PAGADO') {
          supabaseFactura.estado_verificacion = 'PENDIENTE_VERIFICACION';
        }

        logger.debug('🔍 FACTURA TO CREATE:', supabaseFactura);

        // Create in Supabase
        const { data: createdFactura, error: createError } = await supabase
          .from('facturas')
          .insert(supabaseFactura)
          .select()
          .single();

        if (createError) {
          logger.error('🔍 SUPABASE CREATE ERROR DETAILS:', {
            message: createError.message,
            details: createError.details,
            hint: createError.hint,
            code: createError.code
          });
          throw new Error(createError.message || 'Error al crear la factura en Supabase');
        }

        logger.debug('✅ Factura creada en Supabase:', createdFactura);

        // Crédito (módulo Cobros y Créditos): misma fila `creditos` + cuota simple que el módulo espera.
        const metodoCreacion = String(createdFactura.metodo_pago ?? '').toUpperCase();
        const clienteFacturaId = createdFactura.cliente_id as string | null | undefined;
        const totalCreacion = numDb(createdFactura.total);
        const paidCreacion = numDb(createdFactura.monto_pagado);
        const pendienteCreacion = Math.max(0, totalCreacion - paidCreacion);
        if (
          metodoCreacion === 'CREDITO' &&
          clienteFacturaId &&
          totalCreacion > 0 &&
          pendienteCreacion > 0
        ) {
          try {
            const vencRaw = createdFactura.vencimiento as string | null | undefined;
            const fvFallback =
              vencRaw && !Number.isNaN(new Date(vencRaw).getTime())
                ? new Date(vencRaw).toISOString().slice(0, 10)
                : new Date().toISOString().slice(0, 10);
            const config: CreditoCrearConfig =
              factura.creditoCrearConfig ?? { modo: 'simple', fechaVencimientoSimple: fvFallback };
            console.log('=== CALLING crearCreditoTrasFactura ===', {
              facturaId: createdFactura.id,
              configFromFactura: factura.creditoCrearConfig,
              resolvedConfig: config,
            });
            try {
              await crearCreditoTrasFactura(supabase, {
                empresaId,
                facturaId: String(createdFactura.id),
                clienteId: clienteFacturaId,
                total: totalCreacion,
                montoPagado: paidCreacion,
                config,
              });
              console.log('=== crearCreditoTrasFactura SUCCESS ===');
            } catch (err) {
              console.error('=== crearCreditoTrasFactura FAILED ===', err);
            }
          } catch (credWrap) {
            logger.error('❌ Crédito post-factura:', credWrap);
          }
        }

        // Create calendar invite if cliente exists (wizard puede desactivar con agendarCalendario: false)
        const debeAgendar = factura.agendarCalendario !== false;
        if (factura.cliente?.id && debeAgendar) {
          try {
            logger.debug('📅 Creating calendar invite for factura:', createdFactura.id);
          
          const fechaConHora = factura.fechaEntregaAgendada
            ? new Date(factura.fechaEntregaAgendada)
            : (() => {
                const fechaEntrega =
                  factura.fechaEntrega || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
                const horaEntrega = factura.horaAgendada || '14:00';
                const timeParts = horaEntrega.split(':').map(Number);
                const hours = timeParts[0];
                const minutes = timeParts[1];
                const d = new Date(fechaEntrega);
                if (hours !== undefined && minutes !== undefined) {
                  d.setHours(hours, minutes, 0, 0);
                }
                return d;
              })();

            logger.debug('📅 Creating calendar event with delivery time:', {
              fechaConHora: fechaConHora.toISOString()
            });

            const esDomicilio = factura.tipoEntrega === 'DOMICILIO';
            const calendarEvent = {
              empresa_id: empresaId,
              cliente_id: factura.cliente.id,
              titulo: `${esDomicilio ? 'Domicilio agendado' : 'Recogida en tienda'} · ${createdFactura.numero}`,
              descripcion: `${esDomicilio ? 'Entrega a domicilio' : 'Recogida en tienda'} · Factura ${createdFactura.numero} — ${factura.cliente.nombre} — $${createdFactura.total.toLocaleString()}`,
              fecha_inicio: fechaConHora.toISOString(),
              fecha_fin: new Date(fechaConHora.getTime() + 2 * 60 * 60 * 1000).toISOString(), // 2 hours duration
              tipo: 'FACTURA',
              estado: 'PROGRAMADA',
              color: '#3B82F6',
              referencia_factura_id: createdFactura.id,
            };

            const { data: calendarData, error: calendarError } = await supabase
              .from('calendario')
              .insert(calendarEvent)
              .select()
              .single();

            if (calendarError) {
              logger.error('❌ Error creating calendar event:', calendarError);
            } else {
              logger.debug('✅ Calendar event created:', calendarData);
              // Calendar event is now linked to the factura via clienteId and descripcion
              // No need to update factura table - the relationship is maintained in the calendario table
            }
          } catch (calendarError) {
            logger.error('❌ Error in calendar invite creation:', calendarError);
          }
        }
      
      // Convert back to local format
      const nuevaFactura: Factura = {
        id: createdFactura.id,
        numero: createdFactura.numero,
        cliente: factura.cliente || null,
        fechaCreacion: createdFactura.fecha ? new Date(createdFactura.fecha) : new Date(),
        fechaEntrega: createdFactura.vencimiento ? new Date(createdFactura.vencimiento) : new Date(),
        estadoPago: normalizeEstadoPagoAlmacenado(createdFactura.estado_pago),
        estadoEntrega: createdFactura.estado_entrega || 'PENDIENTE_ENTREGA',
        estado: (createdFactura.estado as Factura['estado']) || 'ACTIVA',
        items: factura.items || [],
        subtotalSinIva: createdFactura.subtotal || 0,
        totalIva: createdFactura.impuestos || 0,
        subtotal: createdFactura.subtotal || 0,
        descuento: createdFactura.descuentos || 0,
        total: createdFactura.total || 0,
        notas: createdFactura.notas || '',
        condiciones: '',
        metodoPago: createdFactura.metodo_pago || '', // Fixed: Don't default to EFECTIVO
        fechaPago: createdFactura.fecha_pago ? new Date(createdFactura.fecha_pago) : undefined,
        fechaEntregaReal: createdFactura.fecha_entrega ? new Date(createdFactura.fecha_entrega) : undefined,
        mensajesEnviados: [],
        ultimaActividad: createdFactura.ultima_actividad ? new Date(createdFactura.ultima_actividad) : (createdFactura.updated_at ? new Date(createdFactura.updated_at) : new Date()),
        montoPagado: numDb(createdFactura.monto_pagado),
        montoPendiente: montoPendienteDesdeFila(createdFactura),
        historialPagos: parseHistorialPagos(createdFactura.historial_pagos),
        motivoCancelacion: '',
        fechaCancelacion: undefined,
        tipoEntrega: 'TIENDA' as const,
        direccionEntrega: createdFactura.direccion_entrega || '',
        ciudadEntrega: '',
        barrioEntrega: createdFactura.barrio_entrega || '',
        instruccionesEntrega: createdFactura.instrucciones_entrega || '',
        contactoEntrega: createdFactura.contacto_entrega || '',
        telefonoEntrega: createdFactura.telefono_entrega || '',
        tipoDescuento: createdFactura.tipo_descuento || 'NINGUNO' as const,
        valorDescuento: createdFactura.valor_descuento || 0,
        descripcionDescuento: createdFactura.descripcion_descuento || '',
        citaId: createdFactura.cita_id || undefined,
        fechaAgendada: createdFactura.fecha_agendada ? new Date(createdFactura.fecha_agendada) : undefined,
        fechaEntregaAgendada: createdFactura.fecha_entrega_agendada
          ? new Date(createdFactura.fecha_entrega_agendada)
          : undefined,
        horaAgendada: createdFactura.hora_agendada || '',
        vendedor: createdFactura.vendedor || '',
        canalVenta: createdFactura.canal_venta || 'TIENDA' as const,
        estadoVerificacion: (createdFactura as { estado_verificacion?: Factura['estadoVerificacion'] })
          .estado_verificacion ?? null,
        prioridad: createdFactura.prioridad || 'MEDIA' as const,
        requiereInstalacion: createdFactura.requiere_instalacion || false,
        garantia: createdFactura.garantia || '',
        observacionesInternas: createdFactura.observaciones_internas || '',
        esRegalo: createdFactura.es_regalo || false,
        mensajeRegalo: createdFactura.mensaje_regalo || '',
        direccionRegalo: createdFactura.direccion_regalo || '',
        motivoRegalo: createdFactura.motivo_regalo || '',
        destinatarioRegalo: createdFactura.destinatario_regalo || '',
        ocasionRegalo: createdFactura.ocasion_regalo || 'OTRO' as const,
        fechaOcasion: createdFactura.fecha_ocasion ? new Date(createdFactura.fecha_ocasion) : undefined,
        incluirTarjetaRegalo: createdFactura.incluir_tarjeta_regalo || false,
        mensajeTarjeta: createdFactura.mensaje_tarjeta || '',
        fotoEntrega: createdFactura.foto_entrega ?? null,
      };


        set(state => ({
          facturas: [nuevaFactura, ...state.facturas]
        }));

        logger.debug('✅ Factura creada exitosamente:', nuevaFactura);

        // Facturación electrónica (Matias FEV) — only when the sale opted in
        try {
          const wantsFe = Boolean(
            (factura as { emitirFacturaElectronica?: boolean }).emitirFacturaElectronica
          );
          if (wantsFe) {
            const { emitirFacturaFe } = await import('../lib/facturacionElectronicaApi');
            try {
              const feResult = await emitirFacturaFe(empresaId, String(createdFactura.id));
              (nuevaFactura as { feEmitResult?: unknown }).feEmitResult = feResult;
              if (feResult?.fe_estado) {
                nuevaFactura.feEstado = feResult.fe_estado as Factura['feEstado'];
                nuevaFactura.feCufe = feResult.fe_cufe ?? null;
                nuevaFactura.feError = feResult.fe_error ?? null;
                nuevaFactura.feEmittedAt = new Date();
              }
            } catch (feErr) {
              const msg =
                feErr instanceof Error
                  ? feErr.message
                  : typeof feErr === 'string'
                    ? feErr
                    : 'Error al emitir factura electrónica';
              logger.warn('FE emit failed (factura ya creada):', msg);
              (nuevaFactura as { feEmitError?: string }).feEmitError = msg;
              nuevaFactura.feEstado = 'error';
              nuevaFactura.feError = msg;
            }
            set((state) => ({
              facturas: state.facturas.map((f) => (f.id === nuevaFactura.id ? { ...nuevaFactura } : f)),
            }));
          }
        } catch (feWrap) {
          logger.warn('FE check skipped:', feWrap);
        }

        return nuevaFactura;
      },
      (error) => set({ error }),
      (loading) => set({ loading }),
      {
        errorPrefix: 'Error al crear factura en Supabase'
      }
    );
  },

  updateFactura: async (id, factura) => {
    return handleStoreAction(
      async () => {
        logger.debug('🔄 Updating factura:', id, factura);

        const prev = get().facturas.find((x) => x.id === id);

      // Convert to Supabase format
      const supabaseUpdates: any = {};
      if (factura.canalVenta !== undefined) supabaseUpdates.canal_venta = factura.canalVenta;

      const canalEfectivo = factura.canalVenta ?? prev?.canalVenta ?? 'TIENDA';
      if (factura.numero !== undefined || factura.canalVenta !== undefined) {
        const sourceNum = factura.numero !== undefined ? factura.numero : (prev?.numero ?? '');
        if (String(sourceNum || '').trim()) {
          supabaseUpdates.numero = numeroFacturaParaMostrar(sourceNum, canalEfectivo);
        }
      }
      if (factura.cliente !== undefined) supabaseUpdates.cliente_id = factura.cliente?.id || null;
      if (factura.fechaCreacion !== undefined) supabaseUpdates.fecha = factura.fechaCreacion.toISOString();
      if (factura.fechaEntrega !== undefined) supabaseUpdates.vencimiento = factura.fechaEntrega?.toISOString() || null;
      const wizardTotals = factura.nuevaFlujoTotales;
      if (wizardTotals) {
        supabaseUpdates.subtotal = wizardTotals.base_iva;
        supabaseUpdates.impuestos = wizardTotals.iva;
        supabaseUpdates.descuentos = wizardTotals.descuento;
        supabaseUpdates.total = wizardTotals.total;
      } else {
        if (factura.subtotalSinIva !== undefined) supabaseUpdates.subtotal = factura.subtotalSinIva;
        if (factura.totalIva !== undefined) supabaseUpdates.impuestos = factura.totalIva;
        if (factura.subtotal !== undefined) supabaseUpdates.subtotal = factura.subtotal;
        if (factura.descuento !== undefined) supabaseUpdates.descuentos = factura.descuento;
        if (factura.total !== undefined) supabaseUpdates.total = factura.total;
      }
      if (factura.estado !== undefined) supabaseUpdates.estado = factura.estado;
      if (factura.estadoPago !== undefined) supabaseUpdates.estado_pago = factura.estadoPago;
      if (factura.estadoEntrega !== undefined) supabaseUpdates.estado_entrega = factura.estadoEntrega;
      if (factura.montoPagado !== undefined) supabaseUpdates.monto_pagado = factura.montoPagado;
      if (factura.montoPendiente !== undefined) {
        supabaseUpdates.monto_pendiente = factura.montoPendiente;
        supabaseUpdates.saldo_pendiente = factura.montoPendiente;
      }
      if (factura.historialPagos !== undefined) {
        supabaseUpdates.historial_pagos = historialPagosToJsonDb(factura.historialPagos);
      }
      if (factura.notas !== undefined) supabaseUpdates.notas = factura.notas;
      // Removed condiciones - column doesn't exist in database
      if (factura.metodoPago !== undefined) {
        const mp = factura.metodoPago;
        if (mp == null || String(mp).trim() === '') {
          supabaseUpdates.metodo_pago = null;
        } else {
          supabaseUpdates.metodo_pago = normalizeMetodoPagoDbValue(String(mp)) ?? String(mp);
        }
      }
      if (factura.comprobanteUrl !== undefined) supabaseUpdates.comprobante_url = factura.comprobanteUrl;
      // vendedor column doesn't exist in database - skip this field
      // if (factura.vendedor !== undefined) supabaseUpdates.vendedor = factura.vendedor;
      if (factura.fechaPago !== undefined) supabaseUpdates.fecha_pago = factura.fechaPago?.toISOString() || null;
      if (factura.fechaEntregaReal !== undefined) supabaseUpdates.fecha_entrega = factura.fechaEntregaReal?.toISOString() || null;
      // mensajes_enviados column doesn't exist in database - skip this field
      // if (factura.mensajesEnviados !== undefined) supabaseUpdates.mensajes_enviados = factura.mensajesEnviados;
      if (factura.ultimaActividad !== undefined) supabaseUpdates.ultima_actividad = factura.ultimaActividad.toISOString();
      if (factura.items !== undefined) supabaseUpdates.items = JSON.stringify(factura.items);
      
      // Delivery/shipping fields that DO exist in database
      if (factura.tipoEntrega !== undefined) supabaseUpdates.tipo_entrega = factura.tipoEntrega;
      if (factura.direccionEntrega !== undefined) supabaseUpdates.direccion_entrega = factura.direccionEntrega;
      if (factura.ciudadEntrega !== undefined) supabaseUpdates.ciudad_entrega = factura.ciudadEntrega;
      if (factura.barrioEntrega !== undefined) supabaseUpdates.barrio_entrega = factura.barrioEntrega;
      if (factura.instruccionesEntrega !== undefined) supabaseUpdates.instrucciones_entrega = factura.instruccionesEntrega;
      if (factura.contactoEntrega !== undefined) supabaseUpdates.contacto_entrega = factura.contactoEntrega;
      if (factura.telefonoEntrega !== undefined) supabaseUpdates.telefono_entrega = factura.telefonoEntrega;
      if (factura.horaAgendada !== undefined) supabaseUpdates.hora_agendada = factura.horaAgendada;
      if (factura.fotoEntrega !== undefined) supabaseUpdates.foto_entrega = factura.fotoEntrega;
      if (factura.metodoEntrega !== undefined) supabaseUpdates.metodo_entrega = factura.metodoEntrega;
      if (factura.costoEnvio !== undefined) supabaseUpdates.costo_envio = Math.round(numDb(factura.costoEnvio));
      if (factura.notasEntrega !== undefined) {
        supabaseUpdates.notas_entrega =
          factura.notasEntrega != null && String(factura.notasEntrega).trim() !== ''
            ? String(factura.notasEntrega).trim()
            : null;
      }
      if (factura.ventaCompletada !== undefined) supabaseUpdates.venta_completada = !!factura.ventaCompletada;
      if (factura.ventaCompletadaAt !== undefined) {
        supabaseUpdates.venta_completada_at = factura.ventaCompletadaAt
          ? factura.ventaCompletadaAt.toISOString()
          : null;
      }
      if (factura.estadoVerificacion !== undefined) {
        supabaseUpdates.estado_verificacion = factura.estadoVerificacion;
      }
      if (factura.esRegalo !== undefined) supabaseUpdates.es_regalo = factura.esRegalo;
      if (factura.mensajeRegalo !== undefined) supabaseUpdates.mensaje_regalo = factura.mensajeRegalo || null;
      if (factura.direccionRegalo !== undefined) supabaseUpdates.direccion_regalo = factura.direccionRegalo || null;
      if (factura.fechaEntregaAgendada !== undefined) {
        supabaseUpdates.fecha_entrega_agendada = factura.fechaEntregaAgendada
          ? factura.fechaEntregaAgendada.toISOString()
          : null;
      }
      if (factura.tipoDescuento !== undefined) supabaseUpdates.tipo_descuento = factura.tipoDescuento;
      if (factura.valorDescuento !== undefined) supabaseUpdates.valor_descuento = factura.valorDescuento;
      
        // Always update the updated_at field
        supabaseUpdates.updated_at = new Date().toISOString();

        logger.debug('🔍 FACTURA UPDATES:', supabaseUpdates);
        logger.debug('🔍 ABOUT TO UPDATE FACTURA ID:', id);

        // Update in Supabase
        const { data: updatedFactura, error: updateError } = await supabase
          .from('facturas')
          .update(supabaseUpdates)
          .eq('id', id)
          .select()
          .single();

        if (updateError) {
          logger.error('🔍 SUPABASE UPDATE ERROR DETAILS:', {
            message: updateError.message,
            details: updateError.details,
            hint: updateError.hint,
            code: updateError.code
          });
          throw new Error(updateError.message || 'Error al actualizar la factura en Supabase');
        }

        logger.debug('✅ Factura actualizada en Supabase:', updatedFactura);

        // Crédito: al editar factura, alinear `creditos` + `cuotas` como en alta.
        {
          const metodoUpd = String(updatedFactura.metodo_pago ?? factura.metodoPago ?? '').toUpperCase();
          const clienteUpd = updatedFactura.cliente_id as string | null | undefined;
          const totalUpd = numDb(updatedFactura.total);
          const paidUpd = numDb(updatedFactura.monto_pagado);
          const pendUpd = Math.max(0, totalUpd - paidUpd);
          if (metodoUpd === 'CREDITO' && clienteUpd && totalUpd > 0 && pendUpd > 0) {
            try {
              await sincronizarCreditoTrasUpdateFactura(supabase, {
                facturaId: id,
                empresaId: String(updatedFactura.empresa_id),
                clienteId: clienteUpd,
                metodoPago: updatedFactura.metodo_pago,
                total: totalUpd,
                montoPagado: paidUpd,
                creditoCrearConfig: factura.creditoCrearConfig,
                vencimientoIso: updatedFactura.vencimiento as string | null | undefined,
              });
            } catch (credUpd) {
              logger.error('❌ Crédito post-actualizar factura:', credUpd);
            }
          }
        }

        const empresaId = updatedFactura.empresa_id;
        const prevPago = String(prev?.estadoPago ?? '');
        const nowPago = String(updatedFactura.estado_pago ?? '');
        if (
          empresaId &&
          nowPago === 'PAGADO' &&
          prevPago !== 'PAGADO' &&
          updatedFactura.cliente_id
        ) {
          const { data: cli } = await supabase
            .from('clientes')
            .select('assigned_user_id, nombre, apellido')
            .eq('id', updatedFactura.cliente_id)
            .maybeSingle();
          const rawAssignee = cli?.assigned_user_id != null ? String(cli.assigned_user_id).trim() : '';
          const uuidRe =
            /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
          if (rawAssignee && uuidRe.test(rawAssignee)) {
            const nom = `${cli?.nombre || ''} ${cli?.apellido || ''}`.trim() || 'Cliente';
            const num = String(updatedFactura.numero ?? '');
            invokeSendPush({
              empresa_id: empresaId,
              event_type: 'invoice_paid',
              title: 'Factura pagada',
              body: `Factura ${num} — ${nom} quedó pagada.`,
              tipo: 'factura_pagada',
              usuario_id: rawAssignee,
              data: {
                deep_link: 'clientelo://Facturas',
                factura_id: id,
                cliente_id: updatedFactura.cliente_id as string,
              },
            }).then(() => {});
          }
        }

        // Alerts: mora when estado set to vencida; delivery late when estadoEntrega set to retrasada/no_entregada
        const clienteNombre = factura.cliente
          ? `${factura.cliente.nombre || ''} ${factura.cliente.apellido || ''}`.trim() || 'Cliente'
          : 'Cliente';
        if (String(factura.estado || '').toLowerCase() === 'vencida' && empresaId) {
          const dueDate = factura.fechaEntrega ? new Date(factura.fechaEntrega) : (updatedFactura.vencimiento ? new Date(updatedFactura.vencimiento) : null);
          const daysOverdue = dueDate ? Math.max(0, Math.floor((Date.now() - dueDate.getTime()) / (24 * 60 * 60 * 1000))) : 0;
          createMoraAlert(empresaId, id, clienteNombre, daysOverdue, Number(updatedFactura.total || 0), 'COP').then(() => {});
        }
        const deliveryLateStatuses = ['retrasada', 'RETRASADA', 'no_entregada', 'NO_ENTREGADA'];
        if (factura.estadoEntrega && deliveryLateStatuses.includes(factura.estadoEntrega) && empresaId) {
          const scheduledDate = updatedFactura.fecha_entrega ? new Date(updatedFactura.fecha_entrega) : (factura.fechaEntrega ? new Date(factura.fechaEntrega) : null);
          const scheduledStr = scheduledDate ? scheduledDate.toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10);
          const daysLate = scheduledDate ? Math.max(0, Math.floor((Date.now() - scheduledDate.getTime()) / (24 * 60 * 60 * 1000))) : 0;
          createDeliveryLateAlert(empresaId, id, clienteNombre, scheduledStr, daysLate || undefined).then(() => {});
        }

        const prevEntregaU = String(prev?.estadoEntrega ?? '').toUpperCase();
        const nowEntregaU = String(updatedFactura.estado_entrega ?? '').toUpperCase();
        const prevPagoStr = String(prev?.estadoPago ?? '');
        const nowPagoStr = String(updatedFactura.estado_pago ?? '');
        if (empresaId) {
          if (isEntregaResuelta(nowEntregaU) && !isEntregaResuelta(prevEntregaU)) {
            void deleteAlertsFacturaCondicionResuelta(id, { entregaOk: true });
          }
          if (String(nowPagoStr).toUpperCase() === 'PAGADO' && String(prevPagoStr).toUpperCase() !== 'PAGADO') {
            void deleteAlertsFacturaCondicionResuelta(id, { moraOk: true });
          }
        }
      
      // Ventas are now created ONLY via the "Convertir a Venta" button (moverAVentas)
      
      // Convert back to local format
      const itemsResueltos: Factura['items'] =
        factura.items !== undefined
          ? parseFacturaItems(factura.items)
          : (() => {
              const fromDb = parseFacturaItems(updatedFactura.items);
              if (fromDb.length > 0) return fromDb;
              return prev?.items ?? [];
            })();

      const facturaLocal: Factura = {
        id: updatedFactura.id,
        numero: updatedFactura.numero,
        cliente: factura.cliente !== undefined ? factura.cliente : prev?.cliente ?? null,
        fechaCreacion: new Date(updatedFactura.fecha),
        fechaEntrega: updatedFactura.vencimiento ? new Date(updatedFactura.vencimiento) : new Date(),
        estadoPago: normalizeEstadoPagoAlmacenado(updatedFactura.estado_pago),
        estadoEntrega: updatedFactura.estado_entrega || 'PENDIENTE_ENTREGA',
        estado: (updatedFactura.estado as Factura['estado']) || 'ACTIVA',
        items: itemsResueltos,
        subtotalSinIva: updatedFactura.subtotal || 0,
        totalIva: updatedFactura.impuestos || 0,
        subtotal: updatedFactura.subtotal || 0,
        descuento: updatedFactura.descuentos || 0,
        total: updatedFactura.total || 0,
        notas: updatedFactura.notas || '',
        condiciones: updatedFactura.condiciones || '',
        metodoPago: updatedFactura.metodo_pago || '', // Fixed: Don't default to EFECTIVO
        comprobanteUrl: updatedFactura.comprobante_url || undefined,
        fechaPago: updatedFactura.fecha_pago ? new Date(updatedFactura.fecha_pago) : undefined,
        fechaEntregaReal: updatedFactura.fecha_entrega ? new Date(updatedFactura.fecha_entrega) : undefined,
        mensajesEnviados: [], // mensajes_enviados column doesn't exist in database - use empty array
        ultimaActividad: updatedFactura.ultima_actividad ? new Date(updatedFactura.ultima_actividad) : new Date(),
        montoPagado: numDb(updatedFactura.monto_pagado),
        montoPendiente: montoPendienteDesdeFila(updatedFactura),
        historialPagos: parseHistorialPagos(updatedFactura.historial_pagos),
        motivoCancelacion: updatedFactura.motivo_cancelacion || '',
        fechaCancelacion: updatedFactura.fecha_cancelacion ? new Date(updatedFactura.fecha_cancelacion) : undefined,
        tipoEntrega: updatedFactura.tipo_entrega || 'TIENDA',
        direccionEntrega: updatedFactura.direccion_entrega || '',
        ciudadEntrega: updatedFactura.ciudad_entrega || '',
        barrioEntrega: updatedFactura.barrio_entrega || '',
        instruccionesEntrega: updatedFactura.instrucciones_entrega || '',
        contactoEntrega: updatedFactura.contacto_entrega || '',
        telefonoEntrega: updatedFactura.telefono_entrega || '',
        tipoDescuento: updatedFactura.tipo_descuento || 'NINGUNO',
        valorDescuento: updatedFactura.valor_descuento || 0,
        descripcionDescuento: updatedFactura.descripcion_descuento || '',
        citaId: updatedFactura.cita_id || '',
        fechaAgendada: updatedFactura.fecha_agendada ? new Date(updatedFactura.fecha_agendada) : undefined,
        fechaEntregaAgendada: updatedFactura.fecha_entrega_agendada
          ? new Date(updatedFactura.fecha_entrega_agendada)
          : undefined,
        horaAgendada: updatedFactura.hora_agendada || '',
        vendedor: updatedFactura.vendedor || '',
        canalVenta: updatedFactura.canal_venta || 'TIENDA',
        estadoVerificacion:
          (updatedFactura as { estado_verificacion?: Factura['estadoVerificacion'] }).estado_verificacion ?? null,
        prioridad: updatedFactura.prioridad || 'MEDIA',
        requiereInstalacion: updatedFactura.requiere_instalacion || false,
        garantia: updatedFactura.garantia || '',
        observacionesInternas: updatedFactura.observaciones_internas || '',
        esRegalo: updatedFactura.es_regalo || false,
        mensajeRegalo: updatedFactura.mensaje_regalo || '',
        direccionRegalo: updatedFactura.direccion_regalo || '',
        motivoRegalo: updatedFactura.motivo_regalo || '',
        destinatarioRegalo: updatedFactura.destinatario_regalo || '',
        ocasionRegalo: updatedFactura.ocasion_regalo || 'OTRO',
        fechaOcasion: updatedFactura.fecha_ocasion ? new Date(updatedFactura.fecha_ocasion) : undefined,
        incluirTarjetaRegalo: updatedFactura.incluir_tarjeta_regalo || false,
        mensajeTarjeta: updatedFactura.mensaje_tarjeta || '',
        fotoEntrega: updatedFactura.foto_entrega ?? null,
      };


        // Update local state
        const updatedFacturas = get().facturas.map(f =>
          f.id === id ? facturaLocal : f
        );

        set({ facturas: updatedFacturas });

        logger.debug('✅ Factura actualizada exitosamente:', id);

        return facturaLocal;
      },
      (error) => set({ error }),
      (loading) => set({ loading }),
      {
        errorPrefix: 'Error al actualizar factura en Supabase'
      }
    );
  },

  deleteFactura: async (id) => {
    return handleStoreAction(
      async () => {
        logger.debug('🔄 Deleting factura:', id);

        // Delete from Supabase
        const { error: deleteError } = await supabase
          .from('facturas')
          .delete()
          .eq('id', id);

        if (deleteError) {
          logger.error('🔍 SUPABASE DELETE ERROR DETAILS:', {
            message: deleteError.message,
            details: deleteError.details,
            hint: deleteError.hint,
            code: deleteError.code
          });
          throw deleteError;
        }

        logger.debug('✅ Factura eliminada de Supabase:', id);

        // Remove from local state
        const updatedFacturas = get().facturas.filter(f => f.id !== id);

        set({ facturas: updatedFacturas });

        logger.debug('✅ Factura eliminada exitosamente:', id);
      },
      (error) => set({ error }),
      (loading) => set({ loading }),
      {
        errorPrefix: 'Error al eliminar factura en Supabase'
      }
    );
  },

  registrarPago: async (facturaId, monto, metodoPago) => {
    return handleStoreAction(
      async () => {
        if (!facturaId) {
          throw new Error('Se requiere facturaId — no se puede crear una factura desde registrarPago');
        }
        const m = Number(monto);
        if (!Number.isFinite(m) || m <= 0) {
          throw new Error('Monto inválido');
        }

        const { useAuthStore } = await import('./useAuthStore');
        const empresaId = useAuthStore.getState().currentEmpresa?.id;
        if (!empresaId) {
          throw new Error('No empresa seleccionada');
        }

        const { data: row, error: selErr } = await supabase
          .from('facturas')
          .select('id, empresa_id, total, monto_pagado, estado_pago, metodo_pago')
          .eq('id', facturaId)
          .eq('empresa_id', empresaId)
          .maybeSingle();

        if (selErr) {
          throw selErr;
        }
        if (!row) {
          throw new Error('Factura no encontrada');
        }

        const total = numDb(row.total);
        const pagadoActual = numDb(row.monto_pagado);
        const pendienteAntes = Math.max(0, total - pagadoActual);
        if (m > pendienteAntes + 0.009) {
          throw new Error('El monto supera el saldo pendiente');
        }

        const nuevoPagado = pagadoActual + m;
        const saldo = Math.max(0, total - nuevoPagado);

        let estadoPago: string;
        if (saldo <= 0) {
          estadoPago = 'PAGADO';
        } else if (nuevoPagado > 0) {
          estadoPago = 'PAGO_PARCIAL';
        } else {
          estadoPago = 'PENDIENTE_PAGO';
        }

        const now = new Date().toISOString();
        const metodoNormalizado = normalizeMetodoPagoDbValue(metodoPago ?? '') || metodoPago || null;
        const patch: Record<string, unknown> = {
          monto_pagado: nuevoPagado,
          saldo_pendiente: saldo,
          monto_pendiente: saldo,
          estado_pago: estadoPago,
          updated_at: now,
          metodo_pago: metodoNormalizado || row.metodo_pago || null,
        };
        if (saldo <= 0) {
          patch.fecha_pago = now;
        }

        const { error: upErr } = await supabase
          .from('facturas')
          .update(patch)
          .eq('id', facturaId)
          .eq('empresa_id', empresaId);

        if (upErr) {
          throw upErr;
        }

        if (estadoPago === 'PAGADO') {
          void deleteAlertsFacturaCondicionResuelta(facturaId, { moraOk: true });
        }

        const {
          data: { session },
        } = await supabase.auth.getSession();
        const uid = session?.user?.id ?? null;

        const metodoPagoPago = normalizeMetodoPagoDbValue(metodoPago ?? '') || metodoPago || null;
        const { error: insErr } = await supabase.from('pagos_factura').insert({
          empresa_id: empresaId,
          factura_id: facturaId,
          monto: m,
          metodo_pago: metodoPagoPago,
          creado_por: uid,
        });

        if (insErr) {
          throw insErr;
        }

        await get().fetchFacturas(empresaId);
      },
      (error) => set({ error }),
      (loading) => set({ loading }),
      { errorPrefix: 'Error al registrar pago' }
    );
  },

  verificarPago: async (facturaId) => {
    return handleStoreAction(
      async () => {
        if (!facturaId) throw new Error('Se requiere facturaId');
        const { useAuthStore } = await import('./useAuthStore');
        const empresaId = useAuthStore.getState().currentEmpresa?.id;
        if (!empresaId) throw new Error('No empresa seleccionada');

        const prev = get().facturas.find((x) => x.id === facturaId);
        if (!prev) throw new Error('Factura no encontrada');
        const total = numDb(prev.total);
        const now = new Date().toISOString();

        const { error } = await supabase
          .from('facturas')
          .update({
            estado_pago: 'PAGADO',
            estado_verificacion: 'VERIFICADO',
            monto_pagado: total,
            monto_pendiente: 0,
            saldo_pendiente: 0,
            fecha_pago: now,
            updated_at: now,
          })
          .eq('id', facturaId)
          .eq('empresa_id', empresaId);

        if (error) throw error;
        void deleteAlertsFacturaCondicionResuelta(facturaId, { moraOk: true });
        await get().fetchFacturas(empresaId);
      },
      (error) => set({ error }),
      (loading) => set({ loading }),
      { errorPrefix: 'Error al verificar pago' }
    );
  },

  rechazarPago: async (facturaId, motivo) => {
    return handleStoreAction(
      async () => {
        if (!facturaId) throw new Error('Se requiere facturaId');
        const m = String(motivo || '').trim();
        if (!m) throw new Error('Indica el motivo del rechazo');

        const { useAuthStore } = await import('./useAuthStore');
        const empresaId = useAuthStore.getState().currentEmpresa?.id;
        if (!empresaId) throw new Error('No empresa seleccionada');

        const prev = get().facturas.find((x) => x.id === facturaId);
        const baseNotas = prev?.notas?.trim() || '';
        const suffix = `\n\nPago rechazado: ${m}`;
        const notas = baseNotas ? `${baseNotas}${suffix}` : `Pago rechazado: ${m}`;
        const now = new Date().toISOString();

        const { error } = await supabase
          .from('facturas')
          .update({
            estado_verificacion: 'RECHAZADO',
            notas,
            updated_at: now,
          })
          .eq('id', facturaId)
          .eq('empresa_id', empresaId);

        if (error) throw error;
        await get().fetchFacturas(empresaId);
      },
      (error) => set({ error }),
      (loading) => set({ loading }),
      { errorPrefix: 'Error al rechazar pago' }
    );
  },

  completarVentaPedido: async (facturaId: string, opts) => {
    return handleStoreAction(
      async () => {
        const { supabase } = await import('../lib/supabase');
        const { useAuthStore } = await import('./useAuthStore');
        const empresaId = useAuthStore.getState().currentEmpresa?.id;
        if (!empresaId) throw new Error('No empresa seleccionada');
        const factura = get().facturas.find((f) => f.id === facturaId);
        const saldo = factura ? saldoPendienteFactura(factura) : 0;
        const aCredito = completarVentaDebeCrearCredito(saldo);
        if (aCredito && !factura?.cliente?.id) {
          throw new Error('Para completar con saldo pendiente el pedido necesita un cliente.');
        }

        // Use RPC completar_venta_pedido (SECURITY DEFINER, checks is_empresa_member) - works for staff
        const { error } = await supabase.rpc('completar_venta_pedido', {
          p_factura_id: facturaId,
          p_metodo_pago: aCredito ? 'CREDITO' : null,
        });
        if (error) {
          throw new Error(error.message || 'No se pudo completar la venta');
        }

        await cerrarPedidoEnLineaPorFactura(supabase, {
          empresaId,
          facturaId,
          numero: factura?.numero,
        });
        if (aCredito && factura) {
          await asegurarCreditoTrasCompletarVenta(supabase, {
            empresaId,
            facturaId,
            clienteId: factura.cliente?.id,
            total: Number(factura.total) || 0,
            montoPagado: Number(factura.montoPagado) || 0,
            fechaVencimientoSimple: opts?.fechaVencimientoCredito,
          });
        }

        const now = new Date().toISOString();
        set((state) => ({
          facturas: state.facturas.map((f) =>
            f.id === facturaId
              ? {
                  ...f,
                  ventaCompletada: true,
                  ventaCompletadaAt: new Date(now),
                  estado: 'ARCHIVADA' as Factura['estado'],
                  metodoPago: aCredito ? 'CREDITO' : f.metodoPago,
                }
              : f
          ),
        }));
      },
      (error) => set({ error }),
      (loading) => set({ loading }),
      { errorPrefix: 'Error al completar la venta' }
    );
  },

  eliminarPedido: async (facturaId: string) => {
    return handleStoreAction(
      async () => {
        const { useAuthStore } = await import('./useAuthStore');
        const empresaId = useAuthStore.getState().currentEmpresa?.id;
        if (!empresaId) throw new Error('No empresa seleccionada');
        const now = new Date().toISOString();
        const { error } = await supabase
          .from('facturas')
          .update({
            estado: 'ELIMINADA',
            updated_at: now,
          })
          .eq('id', facturaId)
          .eq('empresa_id', empresaId);
        if (error) throw new Error(error.message || 'No se pudo eliminar el pedido');
        set((state) => ({
          facturas: state.facturas.map((f) =>
            f.id === facturaId ? { ...f, estado: 'ELIMINADA' as Factura['estado'] } : f
          ),
        }));
      },
      (error) => set({ error }),
      (loading) => set({ loading }),
      { errorPrefix: 'Error al eliminar el pedido' }
    );
  },

  marcarEntrega: async (facturaId, estadoEntrega) => {
    return handleStoreAction(
      async () => {
        if (!facturaId) {
          throw new Error('Se requiere facturaId — no se puede crear una factura desde marcarEntrega');
        }
        const { useAuthStore } = await import('./useAuthStore');
        const empresaId = useAuthStore.getState().currentEmpresa?.id;
        if (!empresaId) {
          throw new Error('No empresa seleccionada');
        }

        // Use RPC marcar_factura_entregada (SECURITY DEFINER, checks is_empresa_member) - works for staff
        // Accepted values: ENTREGADO, ENTREGADO_PARCIAL, EN_TIENDA, PENDIENTE_ENTREGA
        const { error } = await supabase.rpc('marcar_factura_entregada', {
          p_factura_id: facturaId,
          p_estado: estadoEntrega,
        });

        if (error) {
          throw error;
        }

        if (isEntregaResuelta(estadoEntrega)) {
          void deleteAlertsFacturaCondicionResuelta(facturaId, { entregaOk: true });
        }

        await get().fetchFacturas(empresaId);
      },
      (error) => set({ error }),
      (loading) => set({ loading }),
      { errorPrefix: 'Error al actualizar entrega' }
    );
  },

  clearError: () => {
    set({ error: null });
  },

  reset: () => {
    set({
      facturas: mockFacturas,
      facturasArchivadas: [],
      productosServicios: mockProductosServicios,
      loading: false,
      error: null,
    });
  },

  archivarFactura: async (id: string) => {
    return handleStoreAction(
      async () => {
        logger.debug('📦 Archiving factura:', id);

        const { supabase } = await import('../lib/supabase');

        const { data: existing } = await supabase
          .from('facturas')
          .select('id, estado, numero, empresa_id')
          .eq('id', id)
          .maybeSingle();

        if (existing?.estado === 'ARCHIVADA') {
          const nowIso = new Date().toISOString();
          await supabase
            .from('facturas')
            .update({
              venta_completada: true,
              venta_completada_at: nowIso,
              updated_at: nowIso,
            })
            .eq('id', id)
            .eq('venta_completada', false);
          set((state) => ({
            facturas: state.facturas.map((f) =>
              f.id === id
                ? {
                    ...f,
                    estado: 'ARCHIVADA' as Factura['estado'],
                    estadoPago: 'PAGADO',
                    ventaCompletada: true,
                    ventaCompletadaAt: new Date(nowIso),
                  }
                : f
            ),
          }));
          logger.debug('✅ Factura ya estaba archivada (UI sincronizada):', id);
          if (existing?.empresa_id) {
            void cerrarPedidoEnLineaPorFactura(supabase, {
              empresaId: existing.empresa_id,
              facturaId: id,
              numero: existing.numero,
            });
          }
          return;
        }

        const {
          data: { session },
        } = await supabase.auth.getSession();
        /** facturas.usuario_archivo → FK auth.users(id), no public.usuarios.id */
        const usuarioArchivoAuth = session?.user?.id ?? null;

        const fechaArchivado = new Date().toISOString();

        const patch = {
          estado: 'ARCHIVADA' as const,
          estado_pago: 'PAGADO' as const,
          venta_completada: true,
          venta_completada_at: fechaArchivado,
          fecha_archivado: fechaArchivado,
          motivo_archivado: 'ARCHIVO_MANUAL',
          usuario_archivo: usuarioArchivoAuth,
          updated_at: fechaArchivado,
        };

        let { error: updateError } = await supabase.from('facturas').update(patch).eq('id', id);

        if (updateError && usuarioArchivoAuth) {
          const { error: retryErr } = await supabase
            .from('facturas')
            .update({ ...patch, usuario_archivo: null })
            .eq('id', id);
          if (!retryErr) {
            updateError = null;
            logger.warn('⚠️ Archivo sin usuario_archivo (FK auth.users):', id);
          }
        }

        if (updateError) {
          const { data: after } = await supabase
            .from('facturas')
            .select('estado')
            .eq('id', id)
            .maybeSingle();
          if (after?.estado === 'ARCHIVADA') {
            set((state) => ({
              facturas: state.facturas.map((f) =>
                f.id === id
                  ? {
                      ...f,
                      estado: 'ARCHIVADA' as Factura['estado'],
                      estadoPago: 'PAGADO',
                      ventaCompletada: true,
                    }
                  : f
              ),
            }));
            logger.debug('✅ Factura archivada en servidor pese al error reportado:', id);
            return;
          }
          logger.error('❌ Error archiving factura:', {
            message: updateError.message,
            code: (updateError as { code?: string }).code,
            details: (updateError as { details?: string }).details,
            hint: (updateError as { hint?: string }).hint,
          });
          throw updateError;
        }

        set((state) => ({
          facturas: state.facturas.map((f) =>
            f.id === id
              ? {
                  ...f,
                  estado: 'ARCHIVADA' as Factura['estado'],
                  estadoPago: 'PAGADO',
                  ventaCompletada: true,
                  ventaCompletadaAt: new Date(fechaArchivado),
                }
              : f
          ),
        }));

        void deleteAlertsFacturaCondicionResuelta(id, { entregaOk: true, moraOk: true });

        if (existing?.empresa_id) {
          void cerrarPedidoEnLineaPorFactura(supabase, {
            empresaId: existing.empresa_id,
            facturaId: id,
            numero: existing.numero,
          });
        }

        logger.debug('✅ Factura archived successfully:', id);
      },
      (error) => set({ error }),
      (loading) => set({ loading }),
      {
        errorPrefix: 'Error al archivar factura'
      }
    );
  },

  fetchFacturasArchivadas: async (empresaId: string) => {
    return handleStoreAction(
      async () => {
        logger.debug('📦 Fetching archived facturas for empresa:', empresaId);

        const { supabase } = await import('../lib/supabase');

        // Fetch ONLY archived facturas
        const { data: supabaseFacturas, error: fetchError } = await supabase
          .from('facturas')
          .select('*')
          .eq('empresa_id', empresaId)
          .eq('estado', 'ARCHIVADA')
          .order('fecha_archivado', { ascending: false });

        if (fetchError) {
          logger.error('❌ Error fetching archived facturas:', fetchError);
          throw fetchError;
        }

        logger.debug('📦 Archived facturas loaded:', supabaseFacturas?.length);

      // Fetch clientes to populate cliente data
      const { data: clientesData } = await supabase
        .from('clientes')
        .select('*')
        .eq('empresa_id', empresaId);

      const clientesMap = new Map(clientesData?.map(c => [c.id, c]) || []);

      const facturasConClientes = (supabaseFacturas || []).map(factura => ({
        ...factura,
        id: factura.id,
        numero: factura.numero,
        fechaCreacion: new Date(factura.fecha),
        fechaEntrega: new Date(factura.fecha_entrega),
        estado: factura.estado,
        cliente: clientesMap.get(factura.cliente_id) || null,
        items: parseFacturaItems(factura.items),
        subtotal: factura.subtotal,
        impuestos: factura.impuestos,
        descuentos: factura.descuentos || 0,
        total: factura.total,
        notas: factura.notas || '',
        metodoPago: factura.metodo_pago,
        diasValidez: factura.dias_validez || 30,
        createdAt: new Date(factura.created_at),
        updatedAt: new Date(factura.updated_at),
        fecha_archivado: factura.fecha_archivado ? new Date(factura.fecha_archivado) : null
      }));

        set({ facturasArchivadas: facturasConClientes });
      },
      (error) => set({ error }),
      (loading) => set({ loading }),
      {
        errorPrefix: 'Error al cargar facturas archivadas'
      }
    );
  },

  deleteArchivadasOldas: async (empresaId: string) => {
    try {
      logger.debug('🗑️ Deleting archived facturas older than 45 days');

      const { supabase } = await import('../lib/supabase');

      // Calculate date 45 days ago
      const fecha45DiasAtras = new Date();
      fecha45DiasAtras.setDate(fecha45DiasAtras.getDate() - 45);

      // Delete archived facturas older than 45 days
      const { data, error } = await supabase
        .from('facturas')
        .delete()
        .eq('empresa_id', empresaId)
        .eq('estado', 'ARCHIVADA')
        .lt('fecha_archivado', fecha45DiasAtras.toISOString());

      if (error) {
        logger.error('❌ Error deleting old archived facturas:', error);
        throw error;
      }

      logger.debug('✅ Deleted old archived facturas:', data);
    } catch (error) {
      logger.error('❌ Error in deleteArchivadasOldas:', error);
    }
  },

  anularFactura: async (facturaId: string) => {
    return handleStoreAction(
      async () => {
        const { useAuthStore } = await import('./useAuthStore');
        const empresaId = useAuthStore.getState().currentEmpresa?.id;
        if (!empresaId) throw new Error('No empresa seleccionada');
        await anularFacturaEnSupabase(facturaId, empresaId);
        set((s) => ({
          facturas: s.facturas.map((f) =>
            f.id === facturaId ? { ...f, estado: 'ANULADA' as Factura['estado'] } : f
          ),
        }));
      },
      (error) => set({ error }),
      (loading) => set({ loading }),
      { errorPrefix: 'Error al anular factura' }
    );
  },
}));
